/**
 * VaultAttachPanel.jsx — Attach vault documents to a resume section item.
 *
 * Designed to be embedded at the bottom of any section card
 * (Experience, Education, Projects, etc.) when it is expanded.
 *
 * Stores attachments as an array inside the section item itself:
 *   exp.vault_refs = [{ id, title, public_uuid, label }]
 *
 * This is the simplest approach — it travels with the resume JSON,
 * syncs via the existing autosave, and requires zero extra APIs.
 *
 * The VaultReference backend table is used by the public recruiter page
 * to look up linked documents — a separate sync (Phase 2 background task).
 *
 * Props:
 *   refs       — current vault_refs array for this item
 *   onUpdate   — callback(newRefs) to update parent item
 *   authToken  — JWT token for vault document list
 *   theme      — 'dark' | 'light'
 *   sectionLabel — e.g. "Experience Letter", "Certificate"
 */
import React, { useState, useEffect, useCallback } from "react";
import { Link2, X, Plus, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";

const CATEGORY_LABELS = {
  certificate:       "Certificate",
  experience_letter: "Experience Letter",
  offer_letter:      "Offer Letter",
  education:         "Education",
  internship:        "Internship",
  award:             "Award",
  project:           "Project",
  patent:            "Patent",
  publication:       "Publication",
  license:           "License",
  identity:          "Identity Document",
  other:             "Other",
};

export default function VaultAttachPanel({ refs = [], onUpdate, authToken, theme }) {
  const isDark = theme === "dark";
  const [open, setOpen] = useState(false);
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [labelEditing, setLabelEditing] = useState(null); // ref id being label-edited

  // Load vault documents when panel opens
  useEffect(() => {
    if (!open || !authToken || docs.length > 0) return;
    setLoadingDocs(true);
    fetch("/api/vault", { headers: { Authorization: `Bearer ${authToken}` } })
      .then(r => r.json())
      .then(data => setDocs(data.documents || []))
      .catch(() => {})
      .finally(() => setLoadingDocs(false));
  }, [open, authToken, docs.length]);

  const isAttached = useCallback((docId) => refs.some(r => r.doc_id === docId), [refs]);

  const handleAttach = (doc) => {
    if (isAttached(doc.id)) return;
    const newRef = {
      doc_id:      doc.id,
      public_uuid: doc.public_uuid,
      title:       doc.title,
      label:       `View ${CATEGORY_LABELS[doc.category] || doc.title}`,
      category:    doc.category,
      is_public:   doc.is_public,
    };
    onUpdate([...refs, newRef]);
  };

  const handleDetach = (docId) => {
    onUpdate(refs.filter(r => r.doc_id !== docId));
  };

  const handleLabelChange = (docId, label) => {
    onUpdate(refs.map(r => r.doc_id === docId ? { ...r, label } : r));
  };

  const attachedDocs = docs.filter(d => refs.some(r => r.doc_id === d.id));
  const unattachedDocs = docs.filter(d => !refs.some(r => r.doc_id === d.id));

  const border   = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.08)";
  const bg0      = isDark ? "#111113"                : "#FAFAFA";
  const bg1      = isDark ? "#18181B"                : "#F3F3F5";
  const textMain = isDark ? "#F4F4F5"                : "#111827";
  const textMute = isDark ? "#71717A"                : "#9CA3AF";
  const brand    = "#7C3AED";

  return (
    <div style={{
      marginTop: 12,
      borderRadius: 10,
      border: `1px solid ${open ? brand + "40" : border}`,
      background: bg0,
      overflow: "hidden",
      transition: "border-color 0.2s ease",
    }}>
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between",
          padding: "9px 14px", background: "none", border: "none",
          cursor: "pointer", gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <ShieldCheck size={13} color={refs.length > 0 ? "#10B981" : brand} />
          <span style={{ fontSize: 12, fontWeight: 600, color: refs.length > 0 ? "#10B981" : textMute }}>
            {refs.length > 0
              ? `${refs.length} document${refs.length > 1 ? "s" : ""} attached`
              : "Attach from Career Vault"}
          </span>
        </div>
        {open ? <ChevronUp size={13} color={textMute} /> : <ChevronDown size={13} color={textMute} />}
      </button>

      {/* Panel body */}
      {open && (
        <div style={{ borderTop: `1px solid ${border}`, padding: "12px 14px" }}>

          {/* Currently attached */}
          {refs.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: textMute, letterSpacing: "0.06em",
                textTransform: "uppercase", marginBottom: 8 }}>
                Attached Documents
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {refs.map(ref => (
                  <div key={ref.doc_id} style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "7px 10px", borderRadius: 8,
                    background: bg1, border: `1px solid ${border}`,
                  }}>
                    <Link2 size={11} color={brand} style={{ flexShrink: 0 }} />
                    {labelEditing === ref.doc_id ? (
                      <input
                        autoFocus
                        value={ref.label}
                        onChange={e => handleLabelChange(ref.doc_id, e.target.value)}
                        onBlur={() => setLabelEditing(null)}
                        onKeyDown={e => e.key === "Enter" && setLabelEditing(null)}
                        style={{
                          flex: 1, fontSize: 11, background: "none",
                          border: "none", outline: "none",
                          color: textMain, fontWeight: 600,
                        }}
                      />
                    ) : (
                      <span
                        onClick={() => setLabelEditing(ref.doc_id)}
                        style={{ flex: 1, fontSize: 11, fontWeight: 600,
                          color: textMain, cursor: "text",
                          borderBottom: `1px dashed ${border}` }}
                        title="Click to edit label"
                      >
                        {ref.label}
                      </span>
                    )}
                    {!ref.is_public && (
                      <span style={{ fontSize: 9, color: "#F59E0B", fontWeight: 700, flexShrink: 0 }}>PRIVATE</span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDetach(ref.doc_id)}
                      style={{ background: "none", border: "none", cursor: "pointer",
                        color: "#EF4444", padding: 2, flexShrink: 0 }}
                    >
                      <X size={11} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available documents to attach */}
          {loadingDocs ? (
            <p style={{ fontSize: 12, color: textMute, textAlign: "center", padding: "8px 0" }}>
              Loading vault documents…
            </p>
          ) : docs.length === 0 ? (
            <p style={{ fontSize: 12, color: textMute, textAlign: "center", padding: "8px 0" }}>
              No documents in your Career Vault yet.
            </p>
          ) : unattachedDocs.length > 0 ? (
            <div>
              <p style={{ fontSize: 10, fontWeight: 700, color: textMute, letterSpacing: "0.06em",
                textTransform: "uppercase", marginBottom: 8 }}>
                {refs.length > 0 ? "Add More" : "Choose Document"}
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {unattachedDocs.map(doc => (
                  <button
                    key={doc.id}
                    type="button"
                    onClick={() => handleAttach(doc)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "7px 10px", borderRadius: 8, textAlign: "left",
                      background: "none", border: `1px dashed ${border}`,
                      cursor: "pointer", transition: "all 0.15s ease",
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = brand;
                      e.currentTarget.style.background = isDark ? "#1A1025" : "#F5F0FF";
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = border;
                      e.currentTarget.style.background = "none";
                    }}
                  >
                    <Plus size={11} color={brand} style={{ flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 11, fontWeight: 500, color: textMain }}>
                      {doc.title}
                    </span>
                    <span style={{ fontSize: 9, fontWeight: 600, color: textMute }}>
                      {CATEGORY_LABELS[doc.category] || doc.category}
                    </span>
                    {!doc.is_public && (
                      <span style={{ fontSize: 9, color: "#F59E0B", fontWeight: 700 }}>PRIVATE</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <p style={{ fontSize: 11, color: "#10B981", textAlign: "center", padding: "4px 0" }}>
              All vault documents are attached ✓
            </p>
          )}
        </div>
      )}
    </div>
  );
}
